import { AddressAutocompleteInput, formatAddress } from '../../common/AddressAutocompleteInput';
import React, { useEffect } from 'react';
import { Box, Button, Input, Stack, Typography } from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';

export interface CompanyInfo {
  name: string;
  phone: string;
  email: string;
  address: string;
  areasServed: string[];
}

interface CompanyInfoStepProps {
  value: CompanyInfo;
  onChange: (info: CompanyInfo) => void;
}

interface OrganizationResponse {
  name?: string;
  company_phone_number?: string;
  company_email?: string;
  main_office_address_1?: string;
  main_office_address_2?: string;
  main_office_city?: string;
  main_office_state?: string;
  main_office_zip?: string;
}

interface AreaServedResponse {
  city: string;
  state: string;
}

export const CompanyInfoStep: React.FC<CompanyInfoStepProps> = ({ value, onChange }) => {
  const { data: orgData } = useQuery<OrganizationResponse>({
    queryKey: ['organization-info'],
    queryFn: async () => {
      const res = await api.get<OrganizationResponse>('/api/organizations/current/');
      return res.data || res;
    },
  });

  const { data: areasData } = useQuery<{ results?: AreaServedResponse[] }>({
    queryKey: ['areas-served'],
    queryFn: async () => {
      const res = await api.get<{ results?: AreaServedResponse[] }>('/api/areas-served/');
      return res.data || res;
    },
  });

  useEffect(() => {
    if (orgData) {
      const address = [
        orgData.main_office_address_1,
        orgData.main_office_address_2,
        orgData.main_office_city,
        orgData.main_office_state,
        orgData.main_office_zip
      ].filter(Boolean).join(', ');

      const areas = areasData?.results?.map(area => `${area.city}, ${area.state}`) || [];

      onChange({
        name: orgData.name || '',
        phone: orgData.company_phone_number || '',
        email: orgData.company_email || '',
        address: address || '',
        areasServed: areas,
      });
    }
  }, [orgData, areasData, onChange]);

  const handleField = (field: keyof CompanyInfo, val: string) => {
    onChange({ ...value, [field]: val });
  };

  return (
    <Box>
      <Typography level="h4" mb={2}>
        Check Your Company Details
      </Typography>
      <Stack spacing={2}>
        <Input
          placeholder="Company Name"
          value={value.name}
          onChange={(e) => handleField('name', e.target.value)}
        />
        <Input
          placeholder="Phone"
          value={value.phone}
          onChange={(e) => handleField('phone', e.target.value)}
        />
        <Input
          placeholder="Email"
          value={value.email}
          onChange={(e) => handleField('email', e.target.value)}
        />
        <AddressAutocompleteInput
          value={value.address}
          onChange={(value) => handleField('address', value)}
          onAddressSelect={(parsed) => handleField('address', formatAddress(parsed))}
        />
        {value.areasServed.length > 0 && (
          <Box>
            <Typography level="body-sm" mb={1}>
              Areas We Found You Serve
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {value.areasServed.map((area) => (
                <Button key={area} size="sm" variant="soft">
                  {area}
                </Button>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default CompanyInfoStep;
